/* eslint-disable react/no-unknown-property */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';
import useTranslate from '~hooks/useTranslate/useTranslate';
import styles from './search.module.scss';
import ModuleTitle from '~atoms/module-title';
import Archive from '~organisms/archive';
import keygen from '~helpers/keygen';
import { sortDateOn, sortIntOn, sortStringOn } from '~helpers/search';
import { getOrderedKeys } from '../../../helpers/getPrioritySortedKeys';
import CustomSelect from '~atoms/custom-select';
import Tabs from '~hooks/tabs';
import { mainSettings } from "../../../constants/site-settings/main";
import VariableComponent from "../../organisms/search/variable"

const Search = ({
  serverData,
  pageSearchOptions,
  showNumberOfResults = true,
  pageContext,
}) => {
  const { page, allMarkets } = pageContext;
  const { data, searchParam } = serverData;
  const [sortQuery] = useState(pageSearchOptions.sort[0]);
  const [searchData, setSearchData] = useState(data);
  const activeMarket = allMarkets && allMarkets[page.market];
  const { template } = page;

  const recommendedCasinos =
    pageContext?.marketSections?.recommended_casinos?.modules[0]?.items;
  const topSearches =
    pageContext?.marketSections?.top_searches?.modules[0]?.items;

  const showRecommendedCasinos =
    mainSettings[template]?.recommendedCasinos ||
    mainSettings?.default?.recommendedCasinos;

  const showTopSearches =
    mainSettings[template]?.topSearches || mainSettings?.default?.topSearches;

  if (activeMarket) activeMarket.market = page?.market;

  const marketPrefix = activeMarket?.path_prefix || '/';
  const showAll = useTranslate('showAll', "showAll");

  const sortHandle = (field, name, type) => {
    // eslint-disable-next-line array-callback-return
    Object.keys(searchData).map((item) => {
      switch (type) {
        case 'date':
          searchData[item].sort(sortDateOn(field));
          break;
        case 'string':
          searchData[item].sort(sortStringOn(field));
          break;
        default:
          searchData[item].sort(sortIntOn(field));
          break;
      }
    });
    setSearchData({ ...searchData });
  };

  const typeTabs = searchData ? [showAll, ...getOrderedKeys(searchData)] : [];
  const getItems = (item) => {
    const titleObj = pageSearchOptions.archiveOptions.titles.find((title) => title.type === item);    
    const items = {
      items: searchData[item],
      model_type: item,
      title: titleObj ? titleObj?.title : '',
      pagination_type: 'load_more',
      ...pageSearchOptions.archiveOptions.includeOptions,
    };

    return (
      <div
        results={items.items.length}
        label={titleObj?.title}
        tabId={item}
        key={keygen()}
      >
        <ModuleTitle
          module={{
            module_title: titleObj?.title,
            module_title_tag: 'h2',
          }}
        />
        <Archive
          module={items}
          loadMore={pageSearchOptions.loadMore}
          page={page}
          gtmClass="archive-gtm"
        />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h1>
        {useTranslate('search_for', 'Search For:')} {searchParam}
      </h1>
      <form>
        <input
          type="text"
          aria-label="Search"
          placeholder={searchParam}
          maxLength="60"
          className={styles.formInput}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              // Do code here
              const searchUrl =
                marketPrefix !== '/'
                  ? `/${marketPrefix}/s?s=${ev.target.value}`
                  : `/s?s=${ev.target.value}`;
              window.location.href = searchUrl;

              ev.preventDefault();
            }
          }}
        />
        <button type="button" aria-label="Search" className={styles.searchButton}>
          <FaSearch title="Search Icon" />
        </button>
      </form>
      {searchData && Object.keys(searchData).length > 0 ? (
        <div className={styles.results}>
          <Tabs
            activeTabClass="searchActive"
            headerClass="searchHeader"
            gtmClass="search-tabs-menu-gtm"
            showNumberOfResults={showNumberOfResults}
            totalResults={data.length}
            HeaderComp={
              <CustomSelect
                items={pageSearchOptions.sort}
                onClick={sortHandle}
                activeItem={sortQuery}
              />
            }
          >
            {typeTabs.map((item) => {
              if (item !== showAll) {
                return getItems(item);
              }
                            
              return (
                <div results={data.length} label={item} tabId="scas" key={keygen()}>
                  {getOrderedKeys(searchData).map((item2) => getItems(item2))}
                </div>
              );
            })}
          </Tabs>
        </div>
      ) : (
        <div className={styles.noData}>
          {useTranslate(
            'no_search_data',
            'No data, please type keyword in textbox above'
          )}
          {(showRecommendedCasinos || showTopSearches) && (
            <VariableComponent
              recommendedCasinos={recommendedCasinos}
              topSearches={topSearches}
              showRecommendedCasinos={showRecommendedCasinos}
              extraClass={styles.fromSearchPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

Search.propTypes = {
  serverData: PropTypes.shape({
    data: PropTypes.arrayOf(),
    searchParam: PropTypes.string,
  }),
  formSearchOptions: PropTypes.shape({
    autoComplete: PropTypes.bool,
    sort: PropTypes.bool,
    limit: PropTypes.number,
    searchButtonComponent: PropTypes.element,
    searchFormRef: PropTypes.element,
    searchFormShowStyle: PropTypes.string,
  }),
  pageSearchOptions: PropTypes.shape({
    useArchive: PropTypes.bool,
    archiveOptions: PropTypes.shape({
      titles: PropTypes.arrayOf(PropTypes.shape({})),
      includeOptions: PropTypes.objectOf(PropTypes.string),
    }),
    tabs: PropTypes.bool,
    tabsOptions: PropTypes.shape({
      children: PropTypes.element,
      showAll: PropTypes.string,
      tabsAlign: PropTypes.string,
    }),
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        type: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    loadMore: PropTypes.shape({
      label: PropTypes.string,
      limit: PropTypes.number,
      increment: PropTypes.number,
      usePrimaryButton: PropTypes.bool,
      useInvertColors: PropTypes.bool,
    }),
  }),
  styles: PropTypes.shape({}),
  showNumberOfResults: PropTypes.bool,
  pageContext: PropTypes.shape({
    page: PropTypes.shape({
      translations: PropTypes.shape({}),
      market: PropTypes.string,
      template: PropTypes.string,
    }),
    allMarkets: PropTypes.shape({}),
    marketSections: PropTypes.shape({
      recommended_casinos: PropTypes.shape({
        modules: PropTypes.arrayOf(
          PropTypes.shape({
            items: PropTypes.arrayOf(
              PropTypes.shape({
                items: PropTypes.arrayOf(PropTypes.shape({})),
              })
            ),
          })
        ),
      }),
      top_searches: PropTypes.shape({
        modules: PropTypes.arrayOf(
          PropTypes.shape({
            items: PropTypes.arrayOf(
              PropTypes.shape({
                title: PropTypes.string,
                id: PropTypes.string,
              })
            ),
          })
        ),
      }),
    }),
  }),
};

export default Search;
